import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, HeartHandshake, Lightbulb } from "lucide-react";

const helpTopics = [
  {
    question: "Where should I start?",
    answer: "Add a child profile first. Then add carers and therapists, create goals, and use the planner to keep appointments and activities together."
  },
  {
    question: "How do goals, activities, and session notes work together?",
    answer: "Create a goal for a child, plan supporting activities, and record observations in session notes. From a session note, you can extract suggested activities when your Premium AI provider is configured."
  },
  {
    question: "Can I import or store therapy records?",
    answer: "Use Documents for files you want to keep. In Session Notes, you can import a PDF into a note, review its Markdown content, and save the observations with the relevant child."
  },
  {
    question: "How do I keep track of appointments and spending?",
    answer: "The Calendar brings appointments, goal dates, activity due dates, and budget periods together. Budgets let you define an allocation and record expenses against it."
  },
  {
    question: "What can I do in Community?",
    answer: "You can share posts, comment, react, and report content. Moderators review reports and can hide, edit, or delete content that violates the community guidelines."
  },
  {
    question: "Which features need Premium?",
    answer: "Premium unlocks expanded limits, Library tools, connected storage, and optional AI features. AI features require you to connect your own supported provider key in Settings."
  }
];

export default function HelpPage() {
  return (
    <div className="space-y-8" data-testid="help-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary">Help Center</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-0 shadow-lg rounded-3xl lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading">
              <BookOpen className="h-5 w-5 text-primary" />
              Frequently asked questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {helpTopics.map((topic, index) => (
                <AccordionItem key={topic.question} value={`help-${index}`}>
                  <AccordionTrigger className="text-left">{topic.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{topic.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-0 shadow-lg rounded-3xl">
            <CardContent className="p-6">
              <HeartHandshake className="mb-4 h-8 w-8 text-primary" />
              <h2 className="font-heading text-xl font-bold">A simple first week</h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>Add your child and care team.</li>
                <li>Record appointments and goals.</li>
                <li>Save notes, documents, and activities.</li>
              </ol>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-3xl">
            <CardContent className="p-6">
              <Lightbulb className="mb-4 h-8 w-8 text-golden-500" />
              <h2 className="font-heading text-xl font-bold">Need a quick overview?</h2>
              <p className="mt-2 text-sm text-muted-foreground">Your Summary page highlights the most important upcoming and in-progress care items.</p>
              <Button asChild variant="outline" className="mt-4 rounded-full">
                <Link to="/overview/summary">Open Summary <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
